import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, ChevronDown } from 'lucide-react';
import { faqCategories, flattenFaqs } from './faqs/faqData';

/**
 * Render a single FAQ row (question + collapsible answer).
 */
function FaqRow({ qa, rowKey, isOpen, onToggle }) {
  return (
    <div
      data-testid={`dashboard-faq-${rowKey}`}
      className="border border-slate-200 rounded-xl overflow-hidden bg-white"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-3 px-3 sm:px-4 py-3 text-left hover:bg-slate-50 transition"
        data-testid={`dashboard-faq-toggle-${rowKey}`}
      >
        <span className="font-medium text-slate-800 text-sm leading-snug">{qa.q}</span>
        <ChevronDown
          className={`w-4 h-4 mt-0.5 text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-3 sm:px-4 pb-3 text-sm text-slate-600 leading-relaxed">
          {qa.a}
        </div>
      )}
    </div>
  );
}

/**
 * Render a category accordion (category header + nested FAQ rows).
 */
function CategoryAccordion({ category, isExpanded, onToggleCategory, openRowKey, onToggleRow, scrollRef }) {
  const Icon = category.icon;
  return (
    <div
      ref={scrollRef}
      className="border border-slate-200 rounded-xl bg-white overflow-hidden scroll-mt-4"
      data-testid={`faq-category-${category.key}`}
    >
      <button
        type="button"
        onClick={onToggleCategory}
        className="w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 sm:py-3.5 text-left hover:bg-slate-50 transition"
        data-testid={`faq-category-toggle-${category.key}`}
      >
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
          <Icon className="w-4 h-4 text-emerald-700 shrink-0" />
          <span className="font-semibold text-slate-900 text-sm truncate">{category.title}</span>
          <span className="text-xs text-slate-400 shrink-0">{category.questions.length}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      {isExpanded && (
        <div className="px-2 sm:px-3 pb-3 pt-1 space-y-2" data-testid={`faq-category-body-${category.key}`}>
          {category.questions.map((qa, idx) => {
            const rowKey = `${category.key}-${idx}`;
            return (
              <FaqRow
                key={rowKey}
                qa={qa}
                rowKey={rowKey}
                isOpen={openRowKey === rowKey}
                onToggle={() => onToggleRow(rowKey)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Render the flat search-results list (one card per matching Q&A, with the
 * source category surfaced as a subtitle so the user keeps context).
 */
function SearchResultsList({ searchResults, openRowKey, onToggleRow }) {
  if (searchResults.length === 0) {
    return <p className="text-sm text-slate-400 py-6 text-center">No matching questions found.</p>;
  }
  return searchResults.map((item) => {
    const rowKey = `${item.catKey}-${item.qIdx}`;
    return (
      <div key={rowKey} className="space-y-1">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 px-1">{item.catTitle}</div>
        <FaqRow
          qa={item}
          rowKey={rowKey}
          isOpen={openRowKey === rowKey}
          onToggle={() => onToggleRow(rowKey)}
        />
      </div>
    );
  });
}

/**
 * Render the full collapsible-category list (no search active).
 */
function CategoryList({ isExpanded, onToggleCategory, openRowKey, onToggleRow, categoryRefs }) {
  return faqCategories.map((category) => (
    <CategoryAccordion
      key={category.key}
      category={category}
      isExpanded={isExpanded(category.key)}
      onToggleCategory={() => onToggleCategory(category.key)}
      openRowKey={openRowKey}
      onToggleRow={onToggleRow}
      scrollRef={(el) => { categoryRefs.current[category.key] = el; }}
    />
  ));
}

const InvestorFAQs = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openRowKey, setOpenRowKey] = useState(null);
  // User explicit toggles override the hash-driven default expansion. Map of
  // categoryKey -> bool. Absent keys fall back to "expand iff matches hash".
  const [userToggles, setUserToggles] = useState({});
  const categoryRefs = useRef({});
  const { hash } = useLocation();
  const hashKey = (hash || '').replace(/^#/, '');

  // Deep-link via URL hash: when the FAQs tab is opened with /#category-key
  // (e.g. clicking the FAQLink chip on a Net Worth card), scroll that
  // category gently into view. Expansion happens declaratively via
  // isExpanded() below — no state mutation needed here.
  //
  // On a cold page load (page.goto('/dashboard/faqs#x')), the InvestorFAQs
  // tree mounts AFTER the URL is parsed, so the ref may not yet be wired the
  // first time the effect runs. Retry via rAF + a generous fallback so the
  // scroll target is found whether mounting is fast (SPA nav) or slow (cold
  // hydration).
  useEffect(() => {
    if (!hashKey) return;
    if (!faqCategories.some((c) => c.key === hashKey)) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12; // ~720ms with rAF (60Hz) as worst case
    const tryScroll = () => {
      if (cancelled) return;
      const el = categoryRefs.current[hashKey];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
    return () => { cancelled = true; };
  }, [hashKey]);

  const isExpanded = (key) => (
    Object.prototype.hasOwnProperty.call(userToggles, key)
      ? userToggles[key]
      : key === hashKey
  );

  // When the search query has content, show a flat results list across all
  // categories. Empty query => null sentinel triggers the category view.
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    return flattenFaqs().filter(
      (item) => item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q),
    );
  }, [searchQuery]);

  const toggleCategory = (key) => {
    const current = isExpanded(key);
    setUserToggles((prev) => ({ ...prev, [key]: !current }));
    setOpenRowKey(null);
  };

  const toggleRow = (rowKey) => setOpenRowKey(openRowKey === rowKey ? null : rowKey);

  return (
    <div className="space-y-6" data-testid="investor-faqs-section">
      <div>
        <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">Investor FAQs</h2>
        <p className="text-sm text-slate-500 mt-1">Find answers to common questions about using MyInvestFolio.</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          data-testid="faq-search-input"
          type="text"
          placeholder="Search FAQs..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setOpenRowKey(null); }}
          className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white focus:outline-hidden focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition"
        />
      </div>
      {searchResults !== null ? (
        <div className="space-y-2" data-testid="faq-search-results">
          <SearchResultsList searchResults={searchResults} openRowKey={openRowKey} onToggleRow={toggleRow} />
        </div>
      ) : (
        <div className="space-y-2" data-testid="faq-category-list">
          <CategoryList
            isExpanded={isExpanded} onToggleCategory={toggleCategory}
            openRowKey={openRowKey} onToggleRow={toggleRow}
            categoryRefs={categoryRefs}
          />
        </div>
      )}
    </div>
  );
};

export default InvestorFAQs;
